import type {
  CircleLayerSpecification,
  FillLayerSpecification,
  FilterSpecification,
  LineLayerSpecification,
  SymbolLayerSpecification,
} from "mapbox-gl";
import type * as GeoJSON from "geojson";

import { FUEL_FILL_COLOR, FUEL_FILL_OPACITY } from "../../constants/fuelLoadPaint";
import { ProjectBoundary } from "../../types/project";

export const MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1IjoiYnVwcG9vODUiLCJhIjoiY2xzaGV6M2d6MjV4ajJudDAwOXo1bjMwcSJ9.0LeBUIZbR4UWWloslEZjlA";

/**
 * Mapbox Static Images URL for a story camera — used for list-tile covers.
 * Pitch is capped at 60 and bearing normalised: the static API is stricter
 * than mapbox-gl about both.
 */
export function coverStaticMapUrl(
  camera: { center: [number, number]; zoom: number; pitch?: number; bearing?: number },
  width = 640,
  height = 320
): string {
  const [lng, lat] = camera.center;
  const zoom = Math.min(Math.max(camera.zoom, 0), 22).toFixed(2);
  const bearing = (((camera.bearing ?? 0) % 360) + 360) % 360;
  const pitch = Math.min(Math.max(camera.pitch ?? 0, 0), 60);
  return (
    "https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/" +
    `${lng.toFixed(5)},${lat.toFixed(5)},${zoom},${bearing.toFixed(0)},${pitch.toFixed(0)}` +
    `/${width}x${height}@2x?access_token=${MAPBOX_ACCESS_TOKEN}&attribution=false&logo=false`
  );
}

/** Accepts a Feature, a strict GeoJSON geometry, or the API's loose boundary shape. */
export type StoryBoundaryInput =
  | GeoJSON.Feature
  | GeoJSON.Geometry
  | ProjectBoundary;

export function boundaryToFeature(
  boundary: StoryBoundaryInput | null | undefined
): GeoJSON.Feature | null {
  if (!boundary) return null;
  if (boundary.type === "Feature") return boundary;
  return { type: "Feature", properties: {}, geometry: boundary as GeoJSON.Geometry };
}

export type FillPaint = NonNullable<FillLayerSpecification["paint"]>;
export type LinePaint = NonNullable<LineLayerSpecification["paint"]>;
export type CirclePaint = NonNullable<CircleLayerSpecification["paint"]>;
export type LineLayout = NonNullable<LineLayerSpecification["layout"]>;
export type SymbolPaint = NonNullable<SymbolLayerSpecification["paint"]>;
export type SymbolLayout = NonNullable<SymbolLayerSpecification["layout"]>;

/**
 * Declarative layer descriptions consumed by StoryMapCanvas. Pages derive
 * these from scene/compare-side state; the canvas stays generic.
 */
export type StoryCanvasLayer =
  | {
      id: string;
      kind: "geojson-line";
      data: GeoJSON.Feature | GeoJSON.FeatureCollection | null;
      paint: LinePaint;
      /** Extra layout properties (line-cap etc.) merged with visibility. */
      layout?: LineLayout;
      /** Mapbox filter expression (e.g. geometry-type splits). */
      filter?: FilterSpecification;
      minzoom?: number;
      visible: boolean;
    }
  | {
      id: string;
      kind: "geojson-symbol";
      data: GeoJSON.Feature | GeoJSON.FeatureCollection | null;
      paint: SymbolPaint;
      layout: SymbolLayout;
      /**
       * Icon image the layout references — registered on the map once by id
       * (SDF images can be tinted per-feature via icon-color).
       */
      image?: { id: string; data: ImageData; sdf?: boolean };
      filter?: FilterSpecification;
      minzoom?: number;
      visible: boolean;
    }
  | {
      id: string;
      kind: "geojson-fill";
      data: GeoJSON.Feature | GeoJSON.FeatureCollection | null;
      fillPaint: FillPaint;
      /** Optional companion outline drawn above the fill. */
      linePaint?: LinePaint;
      /** Layout for the companion outline (line-cap etc.). */
      lineLayout?: LineLayout;
      /**
       * Pattern image the fill paint's `fill-pattern` references — registered
       * on the map once by id (see layerStyles.buildPatternImage).
       */
      pattern?: { id: string; data: ImageData };
      /** Mapbox filter expression (e.g. geometry-type splits). */
      filter?: FilterSpecification;
      visible: boolean;
    }
  | {
      id: string;
      kind: "vector";
      url: string;
      sourceLayer: string;
      paint: FillPaint;
      /** Optional companion outline drawn above the fill. */
      linePaint?: LinePaint;
      /** Pattern image the fill paint's `fill-pattern` references. */
      pattern?: { id: string; data: ImageData };
      minzoom?: number;
      maxzoom?: number;
      tileSize?: number;
      promoteId?: string;
      visible: boolean;
    }
  | {
      id: string;
      kind: "geojson-circle";
      data: GeoJSON.Feature | GeoJSON.FeatureCollection | null;
      paint: CirclePaint;
      /** Mapbox filter expression (e.g. geometry-type splits). */
      filter?: FilterSpecification;
      visible: boolean;
      /** Clicks on this layer's features fire StoryMapCanvas.onLayerClick. */
      interactive?: boolean;
    }
  | {
      id: string;
      kind: "image";
      /** PNG URL (public S3 or presigned) drawn as a raster overlay. */
      url: string;
      coordinates: [
        [number, number],
        [number, number],
        [number, number],
        [number, number]
      ];
      /** raster-opacity (default 0.85). */
      opacity?: number;
      visible: boolean;
    }
  | {
      id: string;
      kind: "canvas-anim";
      canvas: HTMLCanvasElement;
      coordinates: [
        [number, number],
        [number, number],
        [number, number],
        [number, number]
      ];
      /** raster-opacity (default 0.85; the Confidence view uses 1). */
      opacity?: number;
      /**
       * Continuously re-read the canvas (default true, for playback). The
       * print capture passes false: its canvases are drawn once at the final
       * frame, and a static source lets the map reach "idle".
       */
      animate?: boolean;
      visible: boolean;
    };

// ---------------------------------------------------------------------------
// Paints (copied from the project-page layer components; static variants — no
// impacted/selected/recalculating states in stories).
// ---------------------------------------------------------------------------

export const BOUNDARY_LINE_PAINT: LinePaint = {
  "line-color": "#00d4ad",
  "line-width": 2.5,
  "line-opacity": 0.9,
};

export const TREATMENTS_FILL_PAINT: FillPaint = {
  "fill-color": "#ffb74d",
  "fill-opacity": 0.3,
};

export const TREATMENTS_LINE_PAINT: LinePaint = {
  "line-color": "#ffb74d",
  "line-width": 1.5,
};

// Fuel-load fill — same ramp as FuelLoadLayer (src/constants/fuelLoadPaint.ts).
export const FUEL_LOAD_PAINT: FillPaint = {
  "fill-color": FUEL_FILL_COLOR,
  "fill-opacity": FUEL_FILL_OPACITY,
};

// Vegetation fill — same as VegetationLayer's base (non-recalculating) paint.
export const VEGETATION_PAINT: FillPaint = {
  "fill-color": ["coalesce", ["get", "color"], "#cccccc"],
  "fill-opacity": 0.75,
};

// Assets fill — AssetsLayer defaults, simplified (no impacted/selected states).
const ASSET_DEFAULT_COLOR = "#AF0";
export const ASSETS_PAINT: FillPaint = {
  "fill-color": ASSET_DEFAULT_COLOR,
  "fill-opacity": 0.5,
  "fill-outline-color": ASSET_DEFAULT_COLOR,
};

// Urban building states during scene animation playback — the same paints as
// the project page's UrbanBuildingStateLayer: buildings light up orange while
// burning and settle to near-black once burnt out, driven by the per-feature
// `urbanState` feature-state ("burning" | "burnt").
export const URBAN_STATE_FILL_PAINT = {
  "fill-color": [
    "case",
    ["==", ["feature-state", "urbanState"], "burning"], "#ff6b00",
    ["==", ["feature-state", "urbanState"], "burnt"], "#1a1a1a",
    "rgba(0,0,0,0)",
  ],
  "fill-opacity": [
    "case",
    ["==", ["feature-state", "urbanState"], "burning"], 0.9,
    ["==", ["feature-state", "urbanState"], "burnt"], 0.75,
    0,
  ],
} as FillPaint;

export const URBAN_STATE_LINE_PAINT = {
  "line-color": [
    "case",
    ["==", ["feature-state", "urbanState"], "burning"], "#ff4500",
    ["==", ["feature-state", "urbanState"], "burnt"], "#555555",
    "rgba(0,0,0,0)",
  ],
  "line-width": [
    "case",
    ["==", ["feature-state", "urbanState"], "burning"], 2.5,
    ["==", ["feature-state", "urbanState"], "burnt"], 1,
    0,
  ],
} as LinePaint;

// Urban ignition-pathway arcs — the same symbology as the project page's
// UrbanIgnitionEdgesLayer: convection orange / radiation yellow solid arcs,
// ember magenta dashed, an SDF arrowhead tinted per mode. All fade in across
// z14→z15.5 (z15.5→16.5 for arrowheads) — city-wide zooms would drown in them.
export const URBAN_EDGE_SOLID_FILTER: FilterSpecification = [
  "in",
  ["get", "mode"],
  ["literal", ["conv", "rad", "cond", "wildland-rad"]],
];
export const URBAN_EDGE_EMBER_FILTER: FilterSpecification = [
  "in",
  ["get", "mode"],
  ["literal", ["ember", "wildland-ember"]],
];
export const URBAN_EDGE_SOLID_PAINT = {
  "line-color": [
    "match",
    ["get", "mode"],
    "conv", "#ff6b00",
    // Radiation — same symbology whether house-to-house or bushfire→home.
    "rad", "#ffcc00",
    "wildland-rad", "#ffcc00",
    "#888888",
  ],
  "line-width": ["match", ["get", "mode"], "conv", 3, "rad", 2.5, "wildland-rad", 2.5, 2],
  "line-opacity": ["interpolate", ["linear"], ["zoom"], 14, 0, 15.5, 0.9],
} as LinePaint;
export const URBAN_EDGE_EMBER_PAINT = {
  "line-color": "#dc13ff",
  "line-width": 4,
  "line-dasharray": [1, 3],
  "line-opacity": ["interpolate", ["linear"], ["zoom"], 14, 0, 15.5, 1.0],
} as LinePaint;
export const URBAN_EDGE_LINE_LAYOUT = {
  "line-cap": "round",
  "line-join": "round",
} as LineLayout;

export const URBAN_EDGE_ARROW_IMAGE_ID = "story-urban-edge-arrow-icon";
export const URBAN_EDGE_ARROW_LAYOUT = {
  "symbol-placement": "line-center",
  "icon-image": URBAN_EDGE_ARROW_IMAGE_ID,
  "icon-size": 1.25,
  "icon-rotation-alignment": "map",
  "icon-allow-overlap": true,
  "icon-ignore-placement": true,
} as SymbolLayout;
export const URBAN_EDGE_ARROW_PAINT = {
  "icon-color": [
    "match",
    ["get", "mode"],
    "conv", "#ff6b00",
    "rad", "#ffcc00",
    "wildland-rad", "#ffcc00",
    "ember", "#dc13ff",
    "wildland-ember", "#dc13ff",
    "#888888",
  ],
  "icon-halo-color": "#000000",
  "icon-halo-width": 1,
  "icon-opacity": ["interpolate", ["linear"], ["zoom"], 15.5, 0, 16.5, 1],
} as SymbolPaint;

/**
 * Small filled triangle pointing right (+x), registered as an SDF icon so the
 * arrow layer can tint it per feature — same construction as the project
 * page's arrow icon. Null in non-DOM environments.
 */
export function buildUrbanArrowImage(): ImageData | null {
  if (typeof document === "undefined") return null;
  const size = 16;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.moveTo(2, 3);
  ctx.lineTo(size - 2, size / 2);
  ctx.lineTo(2, size - 3);
  ctx.closePath();
  ctx.fill();
  return ctx.getImageData(0, 0, size, size);
}

// Fire history fill — FireHistoryLayer paint (per-feature `color` property).
export const FIRE_HISTORY_PAINT: FillPaint = {
  "fill-color": ["coalesce", ["get", "color"], "#808080"],
  "fill-opacity": 0.75,
};

// Clickable ignition markers for animation-mode partition picking (builder).
// Features carry an `isSelected` boolean property.
export const IGNITION_CIRCLE_PAINT: CirclePaint = {
  "circle-radius": ["case", ["get", "isSelected"], 10, 7],
  "circle-color": ["case", ["get", "isSelected"], "#00d4ad", "#ffffff"],
  "circle-opacity": 0.9,
  "circle-stroke-width": 2,
  "circle-stroke-color": ["case", ["get", "isSelected"], "#ffffff", "#00d4ad"],
};

// Cadastral property boundaries — PropertyBoundaryLayer's paint.
export const PROPERTIES_LINE_PAINT: LinePaint = {
  "line-color": "#388E3C",
  "line-width": 3,
};

// Ignition-regime features — IgnitionsLayer's paints, split by geometry type.
const IGNITION_COLOR = "#73ff00";
export const IGNITIONS_POINT_PAINT: CirclePaint = {
  "circle-radius": 6,
  "circle-color": IGNITION_COLOR,
  "circle-opacity": 0.75,
};
export const IGNITIONS_LINE_PAINT: LinePaint = {
  "line-color": IGNITION_COLOR,
  "line-width": 5,
};
export const IGNITIONS_FILL_PAINT: FillPaint = {
  "fill-color": IGNITION_COLOR,
  "fill-opacity": 0.75,
};

// Custom layers — CustomLayersLayer's per-feature style properties.
const CUSTOM_LAYER_FALLBACK_COLOR = "#1abc9c";
export const CUSTOM_LAYERS_FILL_PAINT: FillPaint = {
  "fill-color": [
    "coalesce",
    ["get", "_customLayerFillColor"],
    CUSTOM_LAYER_FALLBACK_COLOR,
  ],
  "fill-opacity": ["coalesce", ["get", "_customLayerOpacity"], 0.4],
  "fill-outline-color": [
    "coalesce",
    ["get", "_customLayerOutlineColor"],
    CUSTOM_LAYER_FALLBACK_COLOR,
  ],
};
export const CUSTOM_LAYERS_LINE_PAINT: LinePaint = {
  "line-color": [
    "coalesce",
    ["get", "_customLayerFillColor"],
    CUSTOM_LAYER_FALLBACK_COLOR,
  ],
  "line-width": 2.5,
  "line-opacity": ["coalesce", ["get", "_customLayerOpacity"], 0.4],
};
export const CUSTOM_LAYERS_POINT_PAINT: CirclePaint = {
  "circle-radius": 5,
  "circle-color": [
    "coalesce",
    ["get", "_customLayerFillColor"],
    CUSTOM_LAYER_FALLBACK_COLOR,
  ],
  "circle-opacity": ["coalesce", ["get", "_customLayerOpacity"], 0.4],
};

// Risk from — per-feature class colour (riskFromStyles ramp), split by
// geometry. The fill carries its class alpha in `fill`; an author's opacity
// override replaces fill-opacity on top of it.
export const RISK_FROM_FILL_PAINT: FillPaint = {
  "fill-color": ["get", "fill"],
  "fill-opacity": 1,
};
export const RISK_FROM_LINE_PAINT: LinePaint = {
  "line-color": ["get", "color"],
  "line-width": 4,
  "line-opacity": 1,
};
export const RISK_FROM_POINT_PAINT: CirclePaint = {
  "circle-radius": ["get", "radius"],
  "circle-color": ["get", "color"],
  "circle-opacity": 1,
  "circle-stroke-width": 2,
  "circle-stroke-color": "#ffffff",
};

// Inspection — yellow/transparent per-feature colour (InspectionLayer).
export const INSPECTION_FILL_PAINT: FillPaint = {
  "fill-color": ["get", "color"],
  "fill-opacity": 0.6,
};

// The inspection point the author picked, marked on the builder map.
export const INSPECTION_POINT_PAINT: CirclePaint = {
  "circle-radius": 6,
  "circle-color": "#ffff00",
  "circle-stroke-width": 2,
  "circle-stroke-color": "#212b37",
};

// Freehand annotations — per-feature style properties written by the drawing
// tool (FreehandFeatureProperties). Lines are split into three layers by
// lineStyle because a dash array can't vary per feature.
const FREEHAND_FALLBACK_COLOR = "#ff3d6e";
export const FREEHAND_FILL_PAINT: FillPaint = {
  "fill-color": ["coalesce", ["get", "color"], FREEHAND_FALLBACK_COLOR],
  "fill-opacity": ["coalesce", ["get", "fillOpacity"], 0.25],
};
export const FREEHAND_LINE_PAINT: LinePaint = {
  "line-color": ["coalesce", ["get", "color"], FREEHAND_FALLBACK_COLOR],
  "line-width": ["coalesce", ["get", "lineWidth"], 3],
  "line-opacity": 0.95,
};
export const FREEHAND_LINE_LAYOUT: LineLayout = {
  "line-cap": "round",
  "line-join": "round",
};
export const FREEHAND_STYLE_FILTERS: Record<"solid" | "dashed" | "dotted", FilterSpecification> = {
  solid: [
    "any",
    ["!", ["has", "lineStyle"]],
    ["==", ["get", "lineStyle"], "solid"],
  ],
  dashed: ["==", ["get", "lineStyle"], "dashed"],
  dotted: ["==", ["get", "lineStyle"], "dotted"],
};

export const GEOM_POLYGONS: FilterSpecification = [
  "any",
  ["==", ["geometry-type"], "Polygon"],
  ["==", ["geometry-type"], "MultiPolygon"],
];
export const GEOM_LINES: FilterSpecification = [
  "any",
  ["==", ["geometry-type"], "LineString"],
  ["==", ["geometry-type"], "MultiLineString"],
];
export const GEOM_POINTS: FilterSpecification = ["==", ["geometry-type"], "Point"];

/** Darken a #rrggbb hex colour (CustomLayersLayer's outline treatment). */
function darken(hex: string, amount = 0.18): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.max(0, Math.round(((n >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.round(((n >> 8) & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round((n & 0xff) * (1 - amount)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

/** Minimal style metadata a custom layer needs for merging. */
export interface CustomLayerStyleMeta {
  color?: string | null;
  opacity?: number | null;
  features: GeoJSON.FeatureCollection;
}

/**
 * Merge several custom layers into one FeatureCollection with per-feature
 * style properties (the CustomLayersLayer pattern), so one set of mapbox
 * layers renders them all.
 */
export function mergeCustomLayerFeatures(
  layers: CustomLayerStyleMeta[]
): GeoJSON.FeatureCollection | null {
  const features: GeoJSON.Feature[] = [];
  layers.forEach((layer) => {
    const fillColor = layer.color ?? CUSTOM_LAYER_FALLBACK_COLOR;
    const opacity = layer.opacity ?? 0.4;
    const outlineColor = darken(fillColor);
    (layer.features?.features ?? []).forEach((f) => {
      features.push({
        ...f,
        properties: {
          ...f.properties,
          _customLayerFillColor: fillColor,
          _customLayerOutlineColor: outlineColor,
          _customLayerOpacity: opacity,
        },
      });
    });
  });
  if (features.length === 0) return null;
  return { type: "FeatureCollection", features };
}

/**
 * Keep only treatments whose Type is in `types` (absent/empty = all). The
 * live endpoint writes PascalCase `Type`; the frozen bundle writes `type`.
 */
export function filterTreatmentsByType(
  fc: GeoJSON.FeatureCollection | null,
  types: string[] | undefined
): GeoJSON.FeatureCollection | null {
  if (!fc || !types || types.length === 0) return fc;
  const wanted = new Set(types.map((t) => t.toLowerCase()));
  return {
    type: "FeatureCollection",
    features: fc.features.filter((f) => {
      const type = f?.properties?.Type ?? f?.properties?.type;
      return typeof type === "string" && wanted.has(type.toLowerCase());
    }),
  };
}

// ---------------------------------------------------------------------------
// Fire-history colouring (mirrors FireHistoryLayer's per-year hue ramp so the
// story map matches the project page). The raw endpoint/mapData features have
// no `color` property, so decorate before rendering.
// ---------------------------------------------------------------------------

const UNKNOWN_COLOR = "#808080";

export function decorateFireHistory(
  fc: GeoJSON.FeatureCollection | null | undefined
): GeoJSON.FeatureCollection | null {
  if (!fc || !Array.isArray(fc.features)) return null;
  const today = new Date().toISOString().split("T")[0];
  const dated = fc.features.filter(
    (f) =>
      !f?.properties?.DateMissing &&
      f?.properties?.EndDate > "1910-01-01" &&
      f?.properties?.EndDate < today
  );
  const minYear =
    dated.length > 0
      ? Math.min(
          ...dated.map((f) => new Date(f?.properties?.EndDate).getFullYear())
        )
      : new Date().getFullYear();
  const range = 2030 - minYear;

  const colored = fc.features.map((feature) => {
    const props = feature?.properties ?? {};
    let color = UNKNOWN_COLOR;
    if (!props.DateMissing && props.EndDate) {
      const year = new Date(props.EndDate).getFullYear();
      const hue = range > 0 ? (120 * (year - minYear)) / range : 0;
      color = `hsl(${hue % 360}, 100%, 50%)`;
    }
    return { ...feature, properties: { ...props, color } };
  });

  return { type: "FeatureCollection", features: colored };
}

